import React from "react";
import { BarChart3, Users, ShieldCheck, Sparkles, RefreshCw, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { TalentIntelligenceDashboard, TalentMetric } from "../Types/TalentIntelligenceTypes";

interface TalentIntelligenceDashboardProps {
  dashboard: TalentIntelligenceDashboard | null;
  loading: boolean;
  onMetricClick: (metric: TalentMetric) => void;
  onRefresh: () => void;
}

const HeroCard: React.FC<{
  label: string;
  metric: TalentMetric;
  icon: React.ReactNode;
  gradient: string;
  onMetricClick: (metric: TalentMetric) => void;
}> = ({ label, metric, icon, gradient, onMetricClick }) => (
  <button
    type="button"
    onClick={() => onMetricClick({ ...metric, label })}
    className={`group relative overflow-hidden rounded-2xl p-4 text-left text-white shadow-md transition-all hover:shadow-xl hover:-translate-y-0.5 ${gradient}`}
    title={metric.subLabel || "Click to search"}
  >
    <div className="absolute -right-3 -top-3 h-20 w-20 rounded-full bg-white/10 blur-2xl group-hover:scale-125 transition-transform" />
    <div className="relative z-10 flex items-start justify-between">
      <span className="rounded-lg bg-white/20 p-2 backdrop-blur-md">{icon}</span>
      <span className="text-[9px] font-bold uppercase tracking-wider text-white/80">→ Search</span>
    </div>
    <div className="relative z-10 mt-3 flex items-baseline gap-1.5">
      <h4 className="text-3xl font-black">{metric.count.toLocaleString()}</h4>
      <span className="text-sm font-bold opacity-90">{label}</span>
    </div>
    {metric.subLabel && <p className="relative z-10 text-[10px] font-medium text-white/80 italic mt-1">{metric.subLabel}</p>}
  </button>
);

const DistributionPanel: React.FC<{
  dashboard: TalentIntelligenceDashboard;
  onMetricClick: (metric: TalentMetric) => void;
}> = ({ dashboard, onMetricClick }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {dashboard.distributions.map((dist) => (
        <div key={dist.type} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={14} className="text-indigo-500" />
            <h4 className="text-xs font-black text-slate-700 uppercase tracking-wide">
              {dist.type === "experience" ? "Experience" :
               dist.type === "seniority" ? "Seniority" :
               dist.type === "location" ? "Location" :
               dist.type === "industry" ? "Industry" :
               "Availability"}
            </h4>
          </div>
          <div className="flex flex-col gap-1">
            {dist.items.map((item) => {
              const max = Math.max(1, ...dist.items.map((i) => i.count));
              return (
                <button
                  key={`${dist.type}-${item.label}`}
                  type="button"
                  onClick={() => onMetricClick(item)}
                  className="group flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors text-left"
                  title={`${item.count.toLocaleString()} candidates · click to search`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs font-semibold text-slate-600 truncate group-hover:text-indigo-700">
                        {item.label}
                      </span>
                      <span className="text-xs font-black text-slate-800 ml-2">{item.count.toLocaleString()}</span>
                    </div>
                    <div className="h-1 rounded-full bg-slate-100 mt-1 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-400 to-fuchsia-400 rounded-full group-hover:from-indigo-500 group-hover:to-fuchsia-500 transition-colors"
                        style={{ width: `${Math.max(3, (item.count / max) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <ChevronIcon />
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[9px] text-slate-400 font-semibold uppercase tracking-wide">
            Every segment is clickable → applied to search
          </p>
        </div>
      ))}
    </div>
  );
};

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-slate-300 group-hover:text-indigo-500 shrink-0">
    <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const TalentIntelligenceDashboardView: React.FC<TalentIntelligenceDashboardProps> = ({
  dashboard,
  loading,
  onMetricClick,
  onRefresh,
}) => {
  if (loading && !dashboard) {
    return (
      <div className="min-h-[200px] bg-[#F8FAFC] rounded-2xl border border-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
          <p className="text-sm font-semibold">Loading talent intelligence…</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-[200px] rounded-2xl border border-amber-200 bg-amber-50 flex items-center justify-center gap-3 p-6">
        <AlertTriangle size={20} className="text-amber-500" />
        <p className="text-sm font-semibold text-amber-700">Unable to load dashboard</p>
        <button onClick={onRefresh} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold">
          Retry
        </button>
      </div>
    );
  }

  const metrics = [
    { label: "Total Talent", metric: dashboard.totalTalent, icon: <Users className="w-4 h-4" />, gradient: "bg-gradient-to-br from-indigo-500 to-blue-700" },
    { label: "Leadership Pool", metric: dashboard.leadershipPool, icon: <BarChart3 className="w-4 h-4" />, gradient: "bg-gradient-to-br from-fuchsia-500 to-purple-700" },
    { label: "Open to Work", metric: dashboard.openToWork, icon: <CheckCircle2 className="w-4 h-4" />, gradient: "bg-gradient-to-br from-emerald-500 to-teal-700" },
    { label: "Nexgent Verified", metric: dashboard.verified, icon: <ShieldCheck className="w-4 h-4" />, gradient: "bg-gradient-to-br from-sky-500 to-indigo-700" },
    { label: "Recently Updated", metric: dashboard.recentlyUpdated, icon: <RefreshCw className="w-4 h-4" />, gradient: "bg-gradient-to-br from-amber-500 to-orange-600" },
    { label: "New This Week", metric: dashboard.newThisWeek, icon: <Sparkles className="w-4 h-4" />, gradient: "bg-gradient-to-br from-rose-500 to-pink-700" },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* Hero metrics */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
            <Clock size={12} /> Today's Talent Intelligence
          </h3>
          <button onClick={onRefresh} className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {metrics.map(({ label, metric, icon, gradient }) => (
            <HeroCard key={label} label={label} metric={metric} icon={icon} gradient={gradient} onMetricClick={onMetricClick} />
          ))}
        </div>
      </div>

      {/* Distributions */}
      <DistributionPanel dashboard={dashboard} onMetricClick={onMetricClick} />
    </div>
  );
};

export default TalentIntelligenceDashboardView;